// Memoized Components for PracticeTest - Render Isolation Pattern
// Each component only re-renders when its specific props change

import React, { memo, useEffect, useState } from 'react';
import type { Question } from '../../types';

// QuestionView - Only re-renders when question changes
interface QuestionViewProps {
  question: Question | undefined;
  isTranslating: boolean;
  currentLanguage: string;
  t_nested: (key: string) => string;
}

export const QuestionView = memo<QuestionViewProps>(({ question, isTranslating, currentLanguage, t_nested }) => {
  if (!question) return null;

  return (
    <>
      <div className="practice-question-text">
        {question.text}
        {isTranslating && currentLanguage !== 'en' && (
          <div style={{
            fontSize: 'var(--text-sm)',
            color: '#6b7280',
            marginTop: '0.5rem',
            fontStyle: 'italic'
          }}>
            {t_nested('practice.translating')}
          </div>
        )}
      </div>
      {question.imageUrl && (
        <div className="practice-question-image">
          <img
            src={question.imageUrl}
            alt="question visual"
            loading="eager"
            decoding="async"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.style.display = 'none';
              }
            }}
          />
        </div>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.question?.id === nextProps.question?.id &&
    prevProps.isTranslating === nextProps.isTranslating
  );
});

QuestionView.displayName = 'QuestionView';

// AnswerOptions - Only re-renders when selectedAnswer or isAnswered changes
interface AnswerOptionsProps {
  question: Question | undefined;
  selectedAnswer: string | null;
  isAnswered: boolean;
  onAnswer: (answerId: string) => void;
}

export const AnswerOptions = memo<AnswerOptionsProps>(({ question, selectedAnswer, isAnswered, onAnswer }) => {
  const [animatingCorrectId, setAnimatingCorrectId] = useState<string | null>(null);
  const [animatingIncorrectId, setAnimatingIncorrectId] = useState<string | null>(null);
  const lastAnswerTimeRef = React.useRef(0);

  useEffect(() => {
    if (isAnswered && question?.correctAnswerId) {
      setAnimatingCorrectId(question.correctAnswerId);
      const t = setTimeout(() => setAnimatingCorrectId(null), 200);
      return () => clearTimeout(t);
    }
  }, [isAnswered, question?.correctAnswerId]);

  useEffect(() => {
    if (isAnswered && selectedAnswer && question?.correctAnswerId && selectedAnswer !== question.correctAnswerId) {
      setAnimatingIncorrectId(selectedAnswer);
      const t = setTimeout(() => setAnimatingIncorrectId(null), 300);
      return () => clearTimeout(t);
    }
  }, [isAnswered, selectedAnswer, question?.correctAnswerId]);

  if (!question?.options) return null;

  const handleOptionClick = (optionId: string) => {
    if (isAnswered) return;
    const now = Date.now();
    if (now - lastAnswerTimeRef.current <= 200) return;
    lastAnswerTimeRef.current = now;
    onAnswer(optionId);
  };

  return (
    <div className="practice-options-list" role="list">
      {question.options.slice(0, 4).map((option, idx) => {
        const isCorrect = isAnswered && question.correctAnswerId === option.id;
        const isIncorrect = isAnswered && selectedAnswer === option.id && selectedAnswer !== question.correctAnswerId;

        const classes = [
          'practice-option-label',
          selectedAnswer === option.id ? 'selected' : '',
          isCorrect ? 'correct' : '',
          isIncorrect ? 'incorrect' : '',
          animatingCorrectId === option.id ? 'option-correct-animate' : '',
          animatingIncorrectId === option.id ? 'option-incorrect-animate' : ''
        ].filter(Boolean).join(' ');

        return (
          <button
            key={option.id}
            type="button"
            data-option-id={option.id}
            className={`${classes} practice-option-base`}
            onClick={() => handleOptionClick(option.id)}
            disabled={isAnswered}
            aria-pressed={selectedAnswer === option.id}
            aria-label={`Option ${String.fromCharCode(65 + idx)}: ${option.text}`}
          >
            <span className="practice-option-letter">
              {String.fromCharCode(65 + idx)}
            </span>
            <span className="practice-option-text">
              {option.text}
            </span>
          </button>
        );
      })}
    </div>
  );
});

AnswerOptions.displayName = 'AnswerOptions';

// ProgressIndicator - Only re-renders when progress changes
interface ProgressIndicatorProps {
  currentQuestionIndex: number;
  totalQuestions: number;
  questionSubject: string;
  t_nested: (key: string) => string;
  getTranslationHelper: (t: (key: string) => string, key: string, fallback: string) => string;
  getTranslatedTestNameForId: (subject: string) => string;
  headerControls?: React.ReactNode;
}

export const ProgressIndicator = memo<ProgressIndicatorProps>(({
  currentQuestionIndex,
  totalQuestions,
  questionSubject,
  t_nested: _t_nested,
  getTranslationHelper: _getTranslationHelper,
  getTranslatedTestNameForId,
  headerControls
}) => {
  const safeTotal = totalQuestions > 0 ? totalQuestions : 0;
  const currentStep = safeTotal > 0 ? currentQuestionIndex + 1 : 0;
  const progressPercent = safeTotal > 0 ? (currentStep / safeTotal) * 100 : 0;

  return (
    <div className="practice-progress-section">
      <div
        className="practice-progress-wrap"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={safeTotal}
        aria-valuenow={currentStep}
        aria-label="Question progress"
      >
        <div
          className="practice-progress-bar"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      <div className="practice-progress-meta">
        <span className="practice-topic-pill">
          {getTranslatedTestNameForId(questionSubject || '')}
        </span>
        <span className="practice-q-count">
          <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
            {currentQuestionIndex + 1}
            {' / '}
            {totalQuestions}
          </span>
        </span>
      </div>
      {headerControls && (
        <div className="practice-header-controls">
          {headerControls}
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.currentQuestionIndex === nextProps.currentQuestionIndex &&
    prevProps.totalQuestions === nextProps.totalQuestions &&
    prevProps.questionSubject === nextProps.questionSubject &&
    prevProps.headerControls === nextProps.headerControls
  );
});

ProgressIndicator.displayName = 'ProgressIndicator';
